/* eslint-disable max-len */
import { useEffect } from 'react';
import proptypes from 'prop-types';
import {
  arrayOf as arrayType,
  bool as boolType,
  number as numberType,
  params,
  shape as shapeType,
  string as stringType,
  useDataModel
} from '@thd-nucleus/data-sources';
import { useStoreId } from '@thd-nucleus/experience-context';
import {
  useHelmet, TitleTag, MetaTag, ScriptTag, LinkTag
} from '@thd-nucleus/thd-helmet';
import { copy } from '@thd-olt-functional/utils';
import {
  formatDescription,
  formatProductLabel,
  getDynamicOGTags,
  getPrimaryImage,
  getBrandName,
  getBundleVideo
} from '../helpers/product-helpers';
import { getVideoStructuredData } from './structured-data/getVideoStructuredData';
import { getBreadcrumbStructuredData } from './structured-data/getBreadcrumbStructuredData';
import { getProductStructuredData } from './structured-data/getProductStructuredData';

const { bool, string, shape, arrayOf } = proptypes;

const generateBundleInformationTitle = (brandName, productLabel, modelNumber, props) => {
  const { pageNumber, pageTitle } = props || {};
  const parts = [
    brandName,
    productLabel,
    modelNumber,
    pageNumber ? `| Pg${pageNumber}` : null
  ].filter(Boolean);
  const defaultTitle = parts.join(' ');
  return pageTitle?.trim() || `${defaultTitle} - The Home Depot`;
};

const getBundleInformationDescription = (product, props) => {
  const { details, seo } = product || {};
  const { description: detailsDescription } = details || {};
  const { seoDescription } = seo || {};
  const { pageDescription } = props || {};
  const defaultDescription = formatDescription(seoDescription || detailsDescription);
  return pageDescription || defaultDescription;
};

const bundleInformationMetaDataHandler = ({ data, props = {} }) => {
  if (!data?.product) {
    return [];
  }
  const { product, products } = data;
  const {
    itemId,
    details,
    identifiers,
    availabilityType,
    media,
    taxonomy,
    pricing,
    specificationGroup,
    info,
    bundleSpecificationDetails
  } = product || {};

  const isKitchenBundle = bundleSpecificationDetails?.experienceType?.toLowerCase() === 'kitchen';

  const productLabel = formatProductLabel({ identifiers });
  const returnable = info?.returnable;
  const brandName = getBrandName(product, products);
  const title = generateBundleInformationTitle(
    brandName,
    identifiers?.productLabel,
    identifiers?.modelNumber,
    props
  );

  const description = getBundleInformationDescription(product, props);
  const { canonicalUrl: canonicalPath = '' } = identifiers || {};

  const canonical = `https://www.homedepot.com${canonicalPath}`;
  const video = getBundleVideo(product, products);
  const primaryImage = getPrimaryImage({ media });
  const alternate = `android-app://com.thehomedepot/homedepot/product/${itemId}`;
  const dynamicOGTags = getDynamicOGTags({
    props, brandName, identifiers, title, description
  });

  const tags = [
    new TitleTag(title),
    new MetaTag({ name: 'description', content: description, id: 'description' }),
    ...dynamicOGTags,
    new MetaTag({ property: 'og:url', content: canonical, id: 'ogUrl' }),
    new MetaTag({ property: 'og:image', content: primaryImage, id: 'ogImage' }),
    new MetaTag({ property: 'og:site_name', content: 'The Home Depot', id: 'ogSiteName' }),
    new MetaTag({ property: 'fb:admins', content: '100005416960152', id: 'fbAdmins' }),
    new LinkTag({ rel: 'canonical', href: canonical, id: 'canonical' }),
    new LinkTag({ rel: 'alternate', href: alternate, id: 'alternate' })
  ];

  const productStructure = getProductStructuredData({
    aggregateReviews: !isKitchenBundle && !(props?.hideIfHDPPSku && identifiers?.skuClassification === 'hdpp') && data.product?.reviews?.ratingsReviews,
    reviews: !isKitchenBundle && !(props?.hideIfHDPPSku && identifiers?.skuClassification === 'hdpp') && data?.reviews?.Results,
    identifiers: {
      ...identifiers,
      productLabel,
      brandName
    },
    media,
    details,
    pricing,
    specifications: specificationGroup,
    availabilityType,
    schemaExclude: props.schema,
    ppq: props.ppq,
    returnable
  });

  if (!brandName) {
    productStructure.brand = undefined;
  }

  tags.push(new ScriptTag({ content: productStructure, id: 'productStructureData' }));

  const breadcrumbStructuredData = getBreadcrumbStructuredData({
    canonical,
    breadcrumbs: copy(taxonomy?.breadCrumbs || []).data,
    title,
    brandName,
    brandLinkUrl: taxonomy?.brandLinkUrl
  });

  tags.push(new ScriptTag({ content: breadcrumbStructuredData, id: 'breadcrumbStructureData' }));

  if (availabilityType?.discontinued) {
    tags.push(new MetaTag({ name: 'robots', content: 'noindex', id: 'robots' }));
  } else {
    tags.push(new MetaTag({ name: 'robots', content: 'max-image-preview:large', id: 'robots' }));
  }

  if (video?.length) {
    const videoStructuredData = getVideoStructuredData({
      videos: video,
      productLabel
    });
    tags.push(new ScriptTag({ content: videoStructuredData, id: 'videoStructureData' }));
  }

  return tags;
};

const BundleInformationMetadata = ({
  itemId,
  pageDescription,
  pageNumber,
  pageTitle,
  ppq,
  questionsCanonical,
  reviewsCanonical,
  hideIfHDPPSku,
  bundleProduct,
  bundleProducts,
  requestVariables,
  skipProductsRequest
}) => {
  const pageSize = 30;
  const storeId = useStoreId();
  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('bundle-metadata.ready');
  }, []);

  const productOptions = {
    variables: {
      itemId,
      storeId
    },
    skip: skipProductsRequest
  };

  let startIndex = (pageNumber - 1) * pageSize;
  startIndex = pageNumber > 1 ? startIndex + 1 : startIndex;

  const reviewsOptions = {
    variables: {
      itemId,
      ...(pageNumber && {
        startIndex,
        pagesize: pageSize,
        recfirstpage: pageSize
      })
    }
  };

  const { data, variables } = useDataModel('product', productOptions);

  const bundleProductIds = data?.product?.bundleSpecificationDetails?.components;
  const { data: productsData } = useDataModel('products', {
    variables: {
      itemIds: bundleProductIds?.map(({ defaultProductId }) => defaultProductId)
    },
    skip: skipProductsRequest || !bundleProductIds?.map(({ defaultProductId }) => defaultProductId)
  });

  const { data: reviewData } = useDataModel('reviews', reviewsOptions);
  const props = {
    reviewsCanonical,
    pageDescription,
    pageNumber,
    pageTitle,
    questionsCanonical,
    ppq,
    hideIfHDPPSku
  };

  const bundleProductData = skipProductsRequest ? bundleProduct : data?.product;
  const bundleProductsData = skipProductsRequest ? bundleProducts : productsData?.products;
  const bundleProductRequestVariables = skipProductsRequest ? requestVariables : variables;

  const helmetData = {
    product: bundleProductData,
    products: bundleProductsData,
    reviews: !(props?.hideIfHDPPSku && bundleProductData?.identifiers?.skuClassification === 'hdpp') && reviewData?.reviews
  };
  const deps = bundleProductData && bundleProductsData && reviewData?.reviews && bundleProductData?.itemId;
  useHelmet('bundleInformationMetadata', { data: helmetData, props, bundleProductRequestVariables }, bundleInformationMetaDataHandler, [deps]);
  return null;
};

BundleInformationMetadata.displayName = 'BundleInformationMetadata';

BundleInformationMetadata.dataModel = {
  product: params({ itemId: stringType().isRequired() }).shape({
    itemId: stringType(),
    dataSources: stringType(),
    availabilityType: shapeType({
      discontinued: boolType(),
      status: boolType()
    }),
    bundleSpecificationDetails: params({ storeId: stringType(), zipCode: stringType() }).shape({
      experienceType: stringType(),
      components: arrayType(shapeType({
        defaultProductId: stringType()
      }))
    }),
    details: shapeType({
      description: stringType()
    }),
    identifiers: shapeType({
      brandName: stringType(),
      canonicalUrl: stringType(),
      itemId: stringType(),
      modelNumber: stringType(),
      productLabel: stringType(),
      storeSkuNumber: stringType(),
      upcGtin13: stringType(),
      skuClassification: stringType(),
      omsThdSku: stringType()
    }),
    info: shapeType({
      returnable: stringType()
    }),
    media: shapeType({
      image: shapeType({
        url: stringType()
      }).client(),
      images: arrayType(shapeType({
        url: stringType(),
        type: stringType(),
        subType: stringType(),
        sizes: arrayType(stringType())
      })),
      video: arrayType(shapeType({
        shortDescription: stringType(),
        thumbnail: stringType(),
        url: stringType(),
        videoStill: stringType(),
        uploadDate: stringType(),
        dateModified: stringType()
      }))
    }),
    pricing: params({ storeId: stringType() }).shape({
      promotion: shapeType({
        dates: shapeType({
          end: stringType()
        })
      }),
      value: numberType({ float: true }),
      original: numberType({ float: true })
    }),
    reviews: shapeType({
      ratingsReviews: shapeType({
        averageRating: stringType(),
        totalReviews: stringType()
      })
    }),
    seo: shapeType({
      seoKeywords: stringType(),
      seoDescription: stringType()
    }),
    specificationGroup: arrayType(shapeType({
      specifications: arrayType(shapeType({
        specName: stringType(),
        specValue: stringType()
      })),
      specTitle: stringType()
    })),
    taxonomy: shapeType({
      breadCrumbs: arrayType(shapeType({
        label: stringType(),
        url: stringType()
      }))
    })
  }),
  reviews: params({ itemId: stringType().isRequired() }).shape({
    Results: arrayType(shapeType({
      Rating: numberType(),
      ReviewText: stringType(),
      Title: stringType(),
      UserNickname: stringType()
    }))
  }),
  products: params({ itemIds: arrayType(stringType().isRequired()).isRequired() }).arrayOf({
    itemId: stringType(),
    identifiers: shapeType({
      brandName: stringType(),
      productLabel: stringType(),
      productType: stringType()
    }),
    media: shapeType({
      video: arrayType({
        shortDescription: stringType(),
        thumbnail: stringType(),
        url: stringType(),
        videoStill: stringType(),
        uploadDate: stringType(),
        dateModified: stringType()
      })
    })
  })
};

BundleInformationMetadata.propTypes = {
  itemId: string.isRequired,
  reviewsCanonical: bool,
  pageNumber: string,
  pageTitle: string,
  pageDescription: string,
  questionsCanonical: bool,
  ppq: bool,
  hideIfHDPPSku: bool,
  bundleProduct: shape({}),
  bundleProducts: arrayOf(shape()),
  requestVariables: shape({}),
  skipProductsRequest: bool
};

BundleInformationMetadata.defaultProps = {
  reviewsCanonical: false,
  pageNumber: null,
  pageTitle: null,
  pageDescription: null,
  questionsCanonical: false,
  ppq: false,
  hideIfHDPPSku: false,
  bundleProduct: null,
  bundleProducts: null,
  requestVariables: null,
  skipProductsRequest: false
};

export { BundleInformationMetadata };